import HttpCodes from 'http-status-codes';
import _ from 'lodash';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { sqRequestsApi } from '@/sdk/api/RequestsApi';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { headlessCaptureMetadata } from '@/utilities/screenshot.utilities';
import { generateRequestId as apiGenerateRequestId } from '@seeqdev/api';
import type { AxiosError } from 'axios';
import type { StatusCodes } from 'http-status-codes';

/**
 * Determines if the error is an http abort error caused by the AbortController
 *
 * @param error An error object
 * @returns true if the error is an http abort error
 */
export function isHttpAbortError(error: unknown) {
  return _.get(error, 'code') === 'ERR_CANCELED';
}

/**
 * Test if the argument represents a http response object that is the result of a canceled seeq request. If the
 * argument doesn't appear to be a http response object false is returned
 */
export function isCanceled(response: unknown) {
  const responseObj = response as (Response & { xhrStatus: string }) | null | undefined;
  return (
    (responseObj &&
      typeof responseObj === 'object' &&
      'status' in responseObj &&
      // -1 status indicates that a response didn't come back from the server - this can happen during cancellation
      //   because we hang up on the server rather than waiting for it to respond with a 503. For non-async requests
      //   or the initial part of the async request the underlying xhr request is aborted leaving xhrStatus=abort. For
      //   an async request waiting for a websocket response the cancellation will not change the xhrStatus since
      //   the initial request to the server was successful (xhrStatus=complete). xhrStatus=error likely indicates
      //   a network related problem on the client side
      // 503 'Service Unavailable' is used by the backend to indicate canceled requests, but it can also be used
      //   to indicate other errors such as the server being overloaded (CRAB-14207). For cancellations the server will
      //   respond with a 'The request was canceled.' statusMessage that we can test against.
      ((responseObj.status === -1 && (responseObj.xhrStatus === 'abort' || responseObj.xhrStatus === 'complete')) ||
        (responseObj.status === HttpCodes.SERVICE_UNAVAILABLE &&
          _.includes(_.get(responseObj, 'data.statusMessage', ''), 'canceled')) ||
        (responseObj.status === -1 && responseObj.statusText === 'canceled'))) ||
    isHttpAbortError(responseObj)
  );
}

export async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, onTimeout: () => void): Promise<T> {
  let timeoutId: number | null = null;

  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = window.setTimeout(() => {
          onTimeout();
          reject(new Error(`Request timed out after ${timeoutMs}ms`));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }
}

/**
 * Returns true if the response matches the given status code
 *
 * @param response - the response object
 * @param statusCode - http code, such as HttpCodes.BAD_REQUEST
 */
export function isRequestWithStatus(response: unknown, statusCode: StatusCodes): boolean {
  const responseObj = response as AxiosError | null | undefined;
  return responseObj?.status === statusCode || responseObj?.response?.status === statusCode;
}

/**
 * Handles forbidden responses by setting redacted to true. The worksheet watches the redacted state and displays
 * a banner if redacted is true.  Returns a caller-supplied deniedResponse in place of the forbidden response so
 * promise chains can continue and subsequent processing can take place.
 *
 * @param response - the response object
 * @param response.status - the response status code
 * @param response.data.statusMessage - the response message
 * @param deniedResponse - the value to be returned in place of the error for forbidden responses. If the
 * status code was not 403, then the original error response is returned.
 */
export function handleForbidden(response: any, deniedResponse = {}) {
  if (isRequestWithStatus(response, HttpCodes.FORBIDDEN)) {
    return deniedResponse;
  } else {
    return Promise.reject(response);
  }
}

/**
 * Cancels any running request after the preview modal has been closed.
 *
 * @param requestId - The ID of the request to cancel
 * @returns that resolves when the request is cancelled
 */
export function cancelRunningRequest(requestId: string) {
  return sqRequestsApi.cancelRequest({ requestId }).catch(function (response) {
    if (_.get(response, 'status') !== HttpCodes.NOT_FOUND) {
      return Promise.reject(response);
    }
  });
}

/**
 * Generates a request ID by combining a GUID with a prefix grabbed from stateParams
 *
 * @returns (String) the requestId
 */
/** Re-export request ID generation from the API package. */
export function generateRequestId() {
  return apiGenerateRequestId();
}

export function getOrigin() {
  if (headlessRenderMode()) {
    return headlessCaptureMetadata().category ?? '';
  } else if (sqWorkbookStore.isTopic) {
    return SeeqNames.Consumption.Topic;
  } else if (sqWorkbookStore.isVantage) {
    return SeeqNames.Consumption.Vantage;
  } else if (sqWorkbookStore.isAnalysis) {
    return SeeqNames.Origins.Analysis;
  } else if (window.location.pathname.startsWith('/administration/')) {
    return SeeqNames.Consumption.Administration;
  }
  return '';
}

/**
 * This will retrieve the OriginURL used for Data Consumption metric tracking. The logic for determining the url:
 * 1. Check to see if an OriginURL parameter is set that overrides anything else. The OriginURL is used to set the
 * URL for Content that is being rendered.
 * 2. Construct the URL as the view-only URL for the current Analysis, Vantage, or Topic.
 * 3. Use the window.location
 */
export function getOriginUrl(): string {
  const maybeOriginOverride = sqWorkbenchStore.stateParams.originURL;
  const workbookGuid = sqWorkbenchStore.stateParams.workbookId;
  const worksheetGuid = sqWorkbenchStore.stateParams.worksheetId;
  // If this is changed, make sure to update `ReportNotifierJob::runJob`'s construction of its url
  const viewOnlyUrl =
    !_.isNil(workbookGuid) && !_.isNil(worksheetGuid) ? `/view/worksheet/${workbookGuid}/${worksheetGuid}` : null;
  const windowUrl = window.location.pathname + window.location.search;
  return maybeOriginOverride || viewOnlyUrl || windowUrl;
}

/**
 * This will retrieve the OriginLabel used for Data Consumption metric tracking. The logic for determining the label:
 * 1. Check to see if an originLabel parameter is set that overrides anything else. The originLabel is used to set the
 * label for Content that is being rendered. For more info:
 * https://seeq.atlassian.net/wiki/spaces/SQ/pages/2355462313/CRAB-29167+Data+Consumption+OriginLabel+and+OriginURL+Tracking
 * 2. Construct the label from the workbook name and worksheet name, which can be an Analysis, Topic, or Vantage room.
 * 3. Use the document title
 */
export function getOriginLabel(): string {
  const maybeOriginOverride = sqWorkbenchStore.stateParams.originLabel;
  const fallbackLabel = document.title.replace(/ - Seeq$/, '');
  const originLabel =
    maybeOriginOverride ||
    _.chain([sqWorkbookStore.name, sqWorkbookStore.getWorksheetName(sqWorkbenchStore.stateParams.worksheetId)])
      .reject(_.isEmpty)
      .thru((parts) => (parts.length === 2 ? parts.join(' - ') : fallbackLabel))
      .value();

  return encodeURIComponent(
    originLabel.length > 0 && sqWorkbookStore.isLocked ? `${originLabel} [Locked]` : originLabel,
  );
}
